import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useRectEffect} from 'react-viewport-utils';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {desktopAndUp} from '../../../constants/customMedia';
import {FONT_WEIGHTS} from '../../../constants/typography';

const TabCtx = createContext({
  currentTabId: '',
  setCurrentId: (_: string) => {
    // noop
  },
  setActiveHeight: (_: number) => {
    // noop
  },
});

const useUpdateTab = () => {
  const {setCurrentId} = useContext(TabCtx);
  return setCurrentId;
};

interface TabContentBoxProps {
  defaultItem: string;
  defaultHeight?: number;
}

export const TabContentBox: FC<React.PropsWithChildren<TabContentBoxProps>> & {
  useUpdateTab: () => (_: string) => void;
} = ({children, defaultItem, defaultHeight}) => {
  const [wrapperHeight, setActiveHeight] = useState(defaultHeight);
  const [currentTabId, setCurrentId] = useState(defaultItem);

  return (
    <TabCtx.Provider value={{currentTabId, setCurrentId, setActiveHeight}}>
      <TabBoxWrapper data-testid={'productDetailsTabBar'}>
        {children}
      </TabBoxWrapper>
      <Placeholder style={{height: `${wrapperHeight}px`}} />
    </TabCtx.Provider>
  );
};

TabContentBox.useUpdateTab = useUpdateTab;

interface TabContentItemProps {
  content: ReactNode;
  name: string;
  label: ReactNode | ((state: {isActive: boolean}) => ReactNode);
  onClickLabel?: VoidFunction;
  className?: string;
}

const TAB_TRANSITION_TIME = 0.3;

export const TabContentItem = ({
  content,
  name,
  label,
  onClickLabel,
  className,
  ref,
}: TabContentItemProps & {ref?: React.Ref<HTMLButtonElement>}) => {
  const {currentTabId, setCurrentId, setActiveHeight} = useContext(TabCtx);
  const isActive = currentTabId === name;
  const contentRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(
      () => setVisible(isActive),
      TAB_TRANSITION_TIME * 1000,
    );
    return () => clearTimeout(timer);
  }, [name, isActive]);

  useRectEffect(
    rect => {
      if (isActive && rect?.height) {
        setActiveHeight(rect.height);
      }
    },
    contentRef,
    {priority: 'low'},
    [isActive],
  );
  const tabState = isActive ? 'active' : 'inactive';

  const handleOnLabelClick = () => {
    setCurrentId(name);
    onClickLabel?.();
  };

  return (
    <TabItemWrapper data-testid={'TabItemWrapper'} className={className}>
      <TabLabel
        data-testid={`TabLabel_${name}_${tabState}`}
        $active={isActive}
        onClick={handleOnLabelClick}
        ref={ref}
      >
        {typeof label === 'function' ? label({isActive}) : label}
      </TabLabel>
      <TabContent $active={isActive} $visible={visible} ref={contentRef}>
        {content}
      </TabContent>
    </TabItemWrapper>
  );
};

const LABEL_HEIGHT = 50;

const TabBoxWrapper = styled.div`
  padding-top: ${LABEL_HEIGHT}px;
  width: 100%;
  display: flex;
  position: relative;
`;

const TabItemWrapper = styled.section`
  display: contents;
`;

// a sub grid would be way better to handle the labels, but this is not yet supported in many browsers
export const TabLabel = styled.button<{$active: boolean}>`
  margin-top: -${LABEL_HEIGHT}px;
  height: ${LABEL_HEIGHT}px;

  border: 0;
  font-weight: ${FONT_WEIGHTS.w400};
  font-size: 20px;
  transition: color 200ms ease 0s;
  color: ${COLORS.Grey2};
  padding: 10px 20px;
  background: transparent;
  cursor: pointer;
  text-align: center;
  width: 100%;
  ${desktopAndUp(
    css`
      width: auto;
    `,
  )}
  :hover {
    color: ${COLORS.Black};
  }
  ${({$active}) =>
    $active &&
    css`
      color: ${COLORS.Black};
    `}
`;

const hidden = css`
  opacity: 0;
  pointer-events: none;
`;

const TabContent = styled.div<{$active: boolean; $visible: boolean}>`
  transition: opacity ${TAB_TRANSITION_TIME}s ease-in;
  position: absolute;
  left: 0;
  top: 50px;
  width: 100%;
  padding: 10px 20px;
  margin-top: 30px;
  ${({$active}) => !$active && hidden}
  ${({$visible}) => !$visible && `display: none;`}
`;

const Placeholder = styled.div`
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: height 700ms;
  width: 100%;
`;
